///
/// EarthBrowser
///
/// Created by Matt Giger on 4/21/2024
/// Copyright (c) 2024 Geodata Labs
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { matrix } from "@stovokor/math/matrix"
import { quaternion } from "@stovokor/math/quaternion"
import { type GLTF } from "./GLTF"
import type { GLTFNodeType } from "./GLTFTypes"

export class GLTFNode extends NodeBase {
	public node: GLTFNodeType
	public gltf: GLTF
	public name?: string

	constructor(node: GLTFNodeType, gltf: GLTF) {
		super()
		this.node = node
		this.gltf = gltf
		this.name = node.name
	}

	public async loadData(): Promise<void> {
		const node = this.node

		if(node.matrix) {
			this.transform = new matrix(node.matrix)
		} else {
			// glTF 2.0: the local matrix is T * R * S, i.e. scale is applied
			// first, then rotation, then translation. With this engine's
			// row-vector convention (`a.multiply(b)` applies a, then b) that
			// is scale.multiply(rotation).multiply(translation); composing in
			// the other order scaled a child's translation by its own scale
			let transform = matrix.identity
			if(node.scale) {
				transform = matrix.scale(node.scale)
			}
			if(node.rotation) {
				const rotation = new quaternion(node.rotation[0], node.rotation[1], node.rotation[2], node.rotation[3])
				transform = transform.multiplyQuat(rotation)
			}
			if(node.translation) {
				const xlate = node.translation
				transform = transform.multiply(matrix.translate(xlate[0], xlate[1], xlate[2]))
			}
			this.transform = transform
		}

		if(node.mesh !== undefined) {
			const primitives = await this.gltf.loadMesh(node.mesh)
			this.addNode(primitives)
		}

		const cnodes = this.gltf.container.nodes
		if(node.children && cnodes) {
			for(const cidx of node.children) {
				const child = new GLTFNode(cnodes[cidx], this.gltf)
				await child.loadData()
				this.addNode(child)
			}
		}
	}
}